import React from 'react';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { FiInfo, FiFacebook, FiInstagram, FiLinkedin } from 'react-icons/fi';
import ScrollReveal from '../../components/ScrollReveal';
import { useLanguage } from '../../contexts/LanguageContext';


type ContactItemProps = {
    icon: React.ReactNode;
    title: string;
    description: React.ReactNode;
};

const ContactItem: React.FC<ContactItemProps> = ({ icon, title, description }) => {
    return (
        <div className="flex flex-col items-center md:items-start text-center md:text-left mb-6 w-full max-w-sm break-words mx-auto">
            {/* Icon */}
            <div className="bg-[#1DB233] p-4 rounded-full inline-flex items-center justify-center mb-4">
                {icon}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-poppins font-bold text-white mb-2">{title}</h2>

            {/* Description */}
            <p className="text-[#ffffff]/60 font-poppins break-words whitespace-pre-line">{description}</p>
        </div>
    );
};



const ContactSection: React.FC = () => {
    const { tString } = useLanguage();
    return (
        <section
            id='contacto'
            className="w-full bg-[#282A2A]"
        >
            <div className="scroll-mt-40 w-full flex flex-col md:flex-row text-center md:text-left max-w-7xl mx-auto">
                <div className="flex-1 text-white">
                    <ScrollReveal direction="fade" delay={0.1}>
                        <h1 className="text-4xl text-white mb-10 font-encode font-bold">
                            {tString('contact.title')}
                        </h1>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-10">
                        <ScrollReveal direction="left" delay={0.2}>
                            <ContactItem
                                icon={<FiMail className="text-white text-xl" />}
                                title={tString('contact.emailTitle')}
                                description={
                                    <>
                                        <span className="font-bold">{tString('contact.emailUsa')}</span>
                                        {`\n${tString('contact.emailUsaAddress')}\n`}
                                        <span className="font-bold">{tString('contact.emailMex')}</span>
                                        {`\n${tString('contact.emailMexAddress')}`}
                                    </>
                                }
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="right" delay={0.3}>
                            <ContactItem
                                icon={<FiPhone className="text-white text-xl" />}
                                title={tString('contact.phoneTitle')}
                                description={<>
                                    <span className="font-bold">{tString('contact.emailMex')}</span>

                                    {`\n${tString('contact.phoneNumber')}\n`}
                                    <span className="font-bold">{tString('contact.emailUsa')}</span>
                                    {`\n${tString('contact.phoneNumberUSA')}`}
                                </>}
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="left" delay={0.4}>
                            <ContactItem
                                icon={<FiMapPin className="text-white text-xl" />}
                                title={tString('contact.addressTitle')}
                                description={
                                    <>
                                        <span className="font-bold text-sm">{tString('contact.addressCorporate')}</span>
                                        {`\n\t${tString('contact.addressCorporateText')}\n\n`}
                                        <span className="font-bold text-sm">{tString('contact.addressUSA')}</span>
                                        {`\n\t${tString('contact.addressUSAText')}\n\n`}
                                        <span className="font-bold text-sm">{tString('contact.addressOperational')}</span>
                                        {`\n\t${tString('contact.addressOperationalText')}`}
                                    </>
                                }
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="right" delay={0.5}>
                            <ContactItem
                                icon={<FiInfo className="text-white text-xl" />}
                                title={tString('contact.socialMediaTitle')}
                                description={
                                    <div className="flex gap-6 justify-center md:justify-start">
                                        <a
                                            href="https://www.facebook.com/profile.php?id=61562370719224 "
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#ffffff]/60 hover:text-[#1877F2] transition-colors duration-200"
                                        >
                                            <FiFacebook className="text-2xl " />
                                        </a>
                                        <a
                                            href="https://www.instagram.com/greencubelogistics/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#ffffff]/60 hover:text-[#E4405F] transition-colors duration-200"
                                        >
                                            <FiInstagram className="text-2xl" />
                                        </a>
                                        <a
                                            href="https://www.linkedin.com/company/green-cube-logistics-s-a-de-c-v/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#ffffff]/60 hover:text-[#25D366] transition-colors duration-200"
                                        >
                                            <FiLinkedin className="text-2xl" />
                                        </a>
                                    </div>
                                }
                            />
                        </ScrollReveal>
                    </div>
                </div>

                {/* Contact Form */}
                <div className="flex-1 pt-6">
                    <ScrollReveal direction="up" delay={0.6}>
                        <form
                            action="https://formsubmit.co/logistica@greencubelog.com"
                            method="POST"
                            className="bg-[#282A2A] p-0 rounded-lg">
                            {/* Optional hidden inputs */}
                            <input type="hidden" name="_captcha" value="true" />
                            <input type="hidden" name="_subject" value={tString('contact.formSubject')} />
                            <input type="hidden" name="_next" value="https://greencubelog.com/" />

                            <div className="mb-6">
                                <label
                                    htmlFor="email"
                                    className="block text-sm text-left font-medium text-[#ffffff] mb-2"
                                >
                                    {tString('contact.formEmail')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="w-full p-3 text-sm text-[#ffffff] bg-[#ffffff]/20 rounded-md focus:outline-none focus:ring-2 focus:ring-[#282A2A]/20 border border-[#282A2A]/20"
                                    placeholder={tString('contact.formEmailPlaceholder')}
                                    required
                                />
                            </div>

                            <div className="mb-6">
                                <label
                                    htmlFor="message"
                                    className="block text-sm text-left font-medium text-[#ffffff] mb-2"
                                >
                                    {tString('contact.formMessage')}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    className="w-full p-3 text-sm text-[#ffffff] bg-[#ffffff]/20 rounded-md focus:outline-none focus:ring-2 focus:ring-[#282A2A]/20 border border-[#282A2A]/20"
                                    placeholder={tString('contact.formMessagePlaceholder')}
                                    rows={22}
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 bg-[#1DB233] font-poppins font-bold text-white rounded-md hover:bg-[#1DB233]/80 focus:outline-none"
                            >
                                {tString('contact.formSubmit')}
                            </button>
                        </form>
                    </ScrollReveal>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
